// Our color palette - each pair is [lighter, darker] shade
const THEME_COLORS = [
  ["#4B9CD3", "#1A5C93"], // blue
  ["#22C55E", "#15803D"], // green
  ["#71717A", "#3F3F46"], // gray
  ["#8B5CF6", "#5B21B6"], // purple
  ["#EC4899", "#BE185D"], // pink
  ["#F59E0B", "#B45309"], // amber
] as const;

function generatePattern(seed: string, color: string): string {
  const patternHash = Array.from(seed).reduce((acc, char, i) => {
    const charCode = char.charCodeAt(0);
    return (acc << 5) - acc + charCode;
  }, 5381);

  const patternType = Math.abs(patternHash) % 4;

  const baseHash = Array.from(seed).reduce(
    (acc, char, i) => acc + char.charCodeAt(0) * (i + 1),
    0,
  );

  // Generate varied values for patterns
  const angle = Math.abs(baseHash) % 360;
  const size = 40 + (Math.abs(baseHash >> 4) % 80);
  const density = 0.2 + (Math.abs(baseHash >> 8) % 60) / 100; // 0.2-0.8
  const opacity = Math.floor((30 + (Math.abs(baseHash >> 12) % 20)) * 0.7);

  switch (patternType) {
    case 0:
      // Varied diagonal stripes
      const stripeWidth = 2 + (Math.abs(baseHash >> 16) % 6); // 2-8px stripes
      return `repeating-linear-gradient(${angle}deg,
        ${color}${opacity} 0px,
        ${color}${opacity} ${stripeWidth}px,
        transparent ${stripeWidth}px,
        transparent ${size * density}px)`;

    case 1: {
      // Helper function to check blob overlap
      const doesOverlap = (
        x1: number,
        y1: number,
        r1: number,
        existingBlobs: Array<{ x: number; y: number; r: number }>,
        maxOverlaps: number = 1,
        isLargeBlob: boolean = false,
      ) => {
        let overlaps = 0;
        for (const blob of existingBlobs) {
          const dx = x1 - blob.x;
          const dy = y1 - blob.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const minDistance = isLargeBlob
            ? (r1 + blob.r) * 1.5
            : (r1 + blob.r) * 1.2;
          if (distance < minDistance) {
            overlaps++;
            if (overlaps > maxOverlaps) return true;
          }
        }
        return false;
      };

      // Track placed blobs
      const placedBlobs: Array<{ x: number; y: number; r: number }> = [];

      // Large scattered blobs
      const blobCount = 2;
      const blobs = Array.from({ length: blobCount }, (_, i) => {
        const blobSize = 120 + (Math.abs(baseHash >> (i * 4)) % 160); // Sizes from 120-280px
        const radius = blobSize / 2;

        let attempts = 0;
        let x, y;

        do {
          const hashOffset = attempts * 13;
          x = Math.abs(baseHash >> (i * 4 + 16 + hashOffset)) % 800;
          y = Math.abs(baseHash >> (i * 4 + 20 + hashOffset)) % 800;
          attempts++;
        } while (
          attempts < 25 &&
          doesOverlap(x, y, radius, placedBlobs, 0, true)
        );

        placedBlobs.push({ x, y, r: radius });

        return `radial-gradient(circle ${blobSize}px, ${color}${opacity} 50%, transparent 50%) ${x}px ${y}px`;
      });

      // Medium blobs
      const mediumBlobs = Array.from({ length: 3 }, (_, i) => {
        const blobSize = 80 + (Math.abs(baseHash >> (i * 4 + 8)) % 100);
        const radius = blobSize / 2;

        let attempts = 0;
        let x, y;

        do {
          const hashOffset = attempts * 17;
          x = Math.abs(baseHash >> (i * 4 + 32 + hashOffset)) % 800;
          y = Math.abs(baseHash >> (i * 4 + 36 + hashOffset)) % 800;
          attempts++;
        } while (
          attempts < 15 &&
          doesOverlap(x, y, radius, placedBlobs, 1, false)
        );

        placedBlobs.push({ x, y, r: radius });

        return `radial-gradient(circle ${blobSize}px, ${color}${opacity} 50%, transparent 50%) ${x}px ${y}px`;
      });

      return [...blobs, ...mediumBlobs].join(", ") + ` / 1000px 1000px`;
    }

    case 2:
      // Varied crosshatch
      const lineWidth = 1 + (Math.abs(baseHash >> 16) % 4); // 1-5px lines
      const spacing = size * (0.5 + (Math.abs(baseHash >> 20) % 50) / 100); // Varied spacing
      return `repeating-linear-gradient(${angle}deg,
        transparent 0px,
        transparent ${spacing - lineWidth}px,
        ${color}${opacity} ${spacing - lineWidth}px,
        ${color}${opacity} ${spacing}px),
        repeating-linear-gradient(${angle + 90}deg,
        transparent 0px,
        transparent ${spacing - lineWidth}px,
        ${color}${opacity} ${spacing - lineWidth}px,
        ${color}${opacity} ${spacing}px)`;

    case 3: {
      const rotation = Math.abs(baseHash >> 16) % 90;
      const squareSize = 60 + (Math.abs(baseHash >> 20) % 100); // 60-160px squares

      const pattern = `linear-gradient(${45 + rotation}deg,
        ${color}${opacity} 25%,
        transparent 25%,
        transparent 75%,
        ${color}${opacity} 75%) 0 0,
        linear-gradient(${45 + rotation}deg,
        ${color}${opacity} 25%,
        transparent 25%,
        transparent 75%,
        ${color}${opacity} 75%) ${squareSize / 2}px ${squareSize / 2}px`;

      return pattern + ` / ${squareSize}px ${squareSize}px`;
    }

    default:
      return `repeating-linear-gradient(${angle}deg,
        ${color}${opacity} 0px,
        ${color}${opacity} 2px,
        transparent 2px,
        transparent ${size}px)`;
  }
}

export function generateGradient(
  seed: string,
  singleColor: boolean = false,
): string {
  const hash = Array.from(seed).reduce(
    (acc, char, i) => acc + char.charCodeAt(0) * (i + 1),
    0,
  );

  const colorIndex = Math.abs(hash) % THEME_COLORS.length;
  const angle = Math.abs(hash >> 8) % 360;

  const [color1, color2] = THEME_COLORS[colorIndex];

  if (singleColor) {
    return color1;
  }

  // Create base gradient
  const baseGradient = `linear-gradient(${angle}deg, ${color1}20, ${color2}30)`;

  // Generate algorithmic pattern
  const pattern = generatePattern(seed, color1);

  return `${baseGradient}, ${pattern}`;
}
